/**
 * Mentions Parser
 *
 * Pure utilities that mirror the @mention handling in Flutter's
 * comments_bottom_sheet.dart (_handleMentions, _clearMentionSymbol,
 * _clearMention, _selectMention, _insertMentionDecoration).
 *
 * These are deliberately framework-agnostic so they can be reused by
 * other features (e.g. content description editor, chat).
 */

export interface MentionAtCursor {
  /** The in-progress mention text, without the leading '@' (may be empty). */
  mention: string;
  /**
   * Position of the first character of the mention (the character AFTER '@').
   * Matches Flutter's `_mentionStartPosition`.
   */
  startPos: number;
  /**
   * Position one past the last character of the mention (cursor/word end).
   * Matches Flutter's `_mentionEndPosition`.
   */
  endPos: number;
}

/**
 * If the cursor is inside a word that contains an '@', return the current
 * mention token; otherwise null. The word boundary extends to the next space
 * after the cursor, or end-of-string.
 */
export function parseMentionAtCursor(text: string, cursorPos: number): MentionAtCursor | null {
  if (cursorPos < 0) return null;

  let nextBlankSpacePosition = 0;
  if (cursorPos > 0) {
    nextBlankSpacePosition = text.indexOf(' ', cursorPos - 1);
    if (nextBlankSpacePosition === -1) {
      nextBlankSpacePosition = text.length - 1;
    }
  }
  const wordLastCharPos = Math.max(cursorPos, nextBlankSpacePosition);
  const prefixText = text.substring(0, wordLastCharPos);
  const words = prefixText.split(' ');
  if (words.length === 0) return null;
  const wordContainingMention = words[words.length - 1];
  if (!wordContainingMention) return null;
  const atIndex = wordContainingMention.indexOf('@');
  if (atIndex === -1) return null;

  const currentMention = wordContainingMention.substring(atIndex + 1).toLowerCase();
  const endPos = wordLastCharPos;
  const startPos = wordLastCharPos - currentMention.length;
  return { mention: currentMention, startPos, endPos };
}

/**
 * Extract all @mention usernames from a block of text (split on whitespace;
 * mirrors Flutter's processCommentMentionsNotifications).
 */
export function extractMentions(text: string): string[] {
  const result: string[] = [];
  const words = text.split(' ');
  for (const word of words) {
    if (!word) continue;
    const decorationIndex = word.indexOf('@');
    if (decorationIndex < 0) continue;
    const tail = word.substring(decorationIndex + 1);
    const mentions = tail.split('@');
    for (const m of mentions) {
      if (m) result.push(m);
    }
  }
  return result;
}

/**
 * Replace the in-progress mention at [startPos, endPos) with `@username`,
 * adding a space before/after when the surrounding characters are not already
 * spaces. Mirrors Flutter's _selectMention.
 *
 * NOTE: startPos/endPos semantics match Flutter's variables (the '@' was at
 * index `startPos - 1` and `endPos` is one past the last typed character).
 */
export function insertMention(
  text: string,
  startPos: number,
  _endPos: number,
  username: string,
): { text: string; cursorPos: number } {
  const prefixText = text.substring(0, Math.max(0, startPos - 1));
  const suffixText = text.substring(startPos);

  let mentionLength = username.length;
  let addSpaceBefore = false;
  let addSpaceAfter = false;
  if (startPos > 1) {
    addSpaceBefore = text[startPos - 2] !== ' ';
  }
  if (startPos === text.length) {
    addSpaceAfter = true;
  } else if (startPos < text.length) {
    addSpaceAfter = text[startPos] !== ' ';
  }

  const newText =
    `${prefixText}${addSpaceBefore ? ' ' : ''}@${username}${addSpaceAfter ? ' ' : ''}${suffixText}`;

  if (addSpaceBefore) mentionLength++;
  if (addSpaceAfter) mentionLength++;
  const offset = Math.min(mentionLength, newText.length);
  const cursorPos = startPos + offset;
  return { text: newText, cursorPos };
}

/**
 * Remove only the leading `@` character from the in-progress mention.
 * Mirrors Flutter's _clearMentionSymbol.
 */
export function removeMentionSymbol(
  text: string,
  endPos: number,
): { text: string; cursorPos: number } {
  const prefixText = text.substring(0, Math.max(0, endPos - 1));
  const suffixText = text.substring(endPos);
  const newText = prefixText + suffixText;
  const cursorPos = Math.max(0, endPos - 1);
  return { text: newText, cursorPos };
}

/**
 * Remove the in-progress mention entirely (including the leading '@').
 * Mirrors Flutter's _clearMention.
 */
export function removeMention(
  text: string,
  startPos: number,
  endPos: number,
): { text: string; cursorPos: number } {
  const prefixText = text.substring(0, Math.max(0, startPos - 1));
  const suffixText = text.substring(endPos);
  const newText = prefixText + suffixText;
  const cursorPos = Math.max(0, startPos - 1);
  return { text: newText, cursorPos };
}
